import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Carousel, Slide } from '@abyss/web/ui/Carousel';
/* eslint-disable no-nested-ternary */
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import React, { createRef, useEffect, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';

import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useHsidUuid } from '../../hooks/useHsidUuid';
/* eslint-disable @optum-fpc-psx-mobile-apps/no-missing-testid */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import { useLagoon } from '../../hooks/useLagoon';
import { usePingFederateEncryptedPayload } from '../../hooks/usePingFederate';
import { useSnackcardViewAllCount } from '../../hooks/useSnackcardViewAllCount';
import { getLagoonConfigValue } from '../../utils/providerSearch.utils';
import { getCurrentMember, getLoggedInMember } from '../../utils/user.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { phoneOnly } from '../ConstantsStyles';
import { makeAllSlidesAriaHidden } from '../Utils/a11yUtils/a11yUtils';
import {
  NoResultsFoundContainer,
  mixedResultCarouselStyle,
} from './ResultSection.style';
import { VirtualVisitCard } from './VirtualVisitCard';

export const ResultSectionVirtual = ({ showCarouselInMobile = false }) => {
  const phoneScreen = useMediaQuery(phoneOnly);
  const [slidesPerView, setSlidesPerView] = useState(2);
  const [winWidth, setWinWidth] = useState(window.innerWidth);
  const virtualVisitProviders = useLagoon('virtual-visit-providers')();

  const { isExpandViewAllEnabled } = useSnackcardViewAllCount(
    virtualVisitProviders.length
  );
  useEffect(() => {
    let windowWidth = window.innerWidth;
    function handleResize() {
      windowWidth = window.innerWidth;
      setWinWidth(windowWidth);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    if (winWidth > 872) {
      if (winWidth > 872 && virtualVisitProviders?.length >= 3)
        setSlidesPerView(2);
      else if (winWidth < 1250 && virtualVisitProviders?.length === 2)
        setSlidesPerView(3);
      else if (winWidth < 1250 && virtualVisitProviders?.length > 2)
        setSlidesPerView(2);
      else if (winWidth > 1250 && virtualVisitProviders?.length >= 2)
        setSlidesPerView(3);
      else if (virtualVisitProviders?.length >= 1) setSlidesPerView(4);
    }
    if (winWidth < 872) setSlidesPerView(1);
    if (
      isExpandViewAllEnabled &&
      winWidth > 868 &&
      (virtualVisitProviders?.length === 1 ||
        virtualVisitProviders?.length === 2)
    )
      setSlidesPerView(2);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [winWidth, isExpandViewAllEnabled]);
  const hsidUuid = useHsidUuid();

  const [encryptedPayload, setEncryptedPayload] = useState<string>('');
  const [, getPingFederatePayload] = usePingFederateEncryptedPayload({
    onCompleted: (result) => {
      setEncryptedPayload(result?.data?.pingFederate?.ref || '');
    },
  });
  const [providerClicked, setProviderClicked] = useState<string>('');

  const loggedInMember = getLoggedInMember();
  const currentMember = getCurrentMember();
  const [memberId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MEMBER_ID,
    0
  );

  const [callPingFederate] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PING_FEDERATE_VC,
  ]);

  const allConfigs = useLagoon('config');
  const ssoOutUrl = getLagoonConfigValue(allConfigs, 'PF_SSOOUT_URL');

  const pfidAdapterId = getLagoonConfigValue(allConfigs, 'PFID_ADAPTER_ID');

  const vsid = getLagoonConfigValue(allConfigs, 'PFID_VS_ID');

  const prodSsoVendors = getLagoonConfigValue(
    allConfigs,
    'ENABLE_PROD_SSO_VENDORS'
  );

  const cardCTA = (provider: any) => {
    let analyticsUrl = provider.ctaUrl;
    if (
      callPingFederate &&
      provider.ssoUrl &&
      provider.partnerSpId &&
      provider.vendorId
    ) {
      analyticsUrl = provider.ssoUrl;
      setProviderClicked(provider.key);
      getPingFederatePayload({
        variables: {
          pingFederatePayload: {
            subject: loggedInMember?.rallyId,
            firstName: currentMember?.demographics?.name.firstName,
            lastName: currentMember?.demographics?.name.lastName,
            dob: currentMember?.demographics?.dateOfBirth,
            groupNumber: currentMember?.policyNumber,
            memberId: memberId?.toString(),
            gender: currentMember?.demographics?.gender,
            state: currentMember?.demographics?.address?.[0]?.state,
            zipCode: currentMember?.demographics?.address?.[0]?.zipCode,
            city: currentMember?.demographics?.address?.[0]?.city,
            address1: currentMember?.demographics?.address?.[0]?.address1,
            useProd: prodSsoVendors
              ?.split(',')
              .map((v) => v.trim())
              .includes(provider.key),
          },
          ...(hsidUuid && { userId: hsidUuid }), // Pass hsidUuid as userId if available
        },
      });
    } else {
      window.open(provider.ctaUrl);
    }

    adobeLinkTrackEvent({
      name: `${provider.key} button`,
      location: `body:virtual care visits ${provider.key}`,
      destinationUrl: analyticsUrl,
    });
  };

  const refs: { [key: string]: React.RefObject<HTMLButtonElement> } = {};

  useEffect(() => {
    if (encryptedPayload) {
      Object.keys(refs).forEach((key) => {
        if (providerClicked === key) {
          refs[key].current?.click();
          setEncryptedPayload('');
          setProviderClicked('');
        }
      });
    }
  }, [encryptedPayload]);

  const getVariableForProvider = (provider, varsToChange) => {
    const tempSsoOutUrl = getLagoonConfigValue(
      allConfigs,
      `PF_SSOOUT_URL_${provider.key}`
    );
    varsToChange.providerSsoOutUrl =
      tempSsoOutUrl?.length > 0
        ? tempSsoOutUrl
        : varsToChange.providerSsoOutUrl;
    const tempVsId = getLagoonConfigValue(
      allConfigs,
      `PFID_VS_ID_${provider.key}`
    );
    varsToChange.vsid = vsid?.length > 0 ? tempVsId : varsToChange.vsid;
  };

  const virtualVisitCards = virtualVisitProviders.map((provider) => {
    const varsToChange = {
      providerSsoOutUrl: ssoOutUrl,
      vsid,
    };
    if (provider.ssoUrl && provider.partnerSpId && provider.vendorId) {
      refs[provider.key] = createRef<HTMLButtonElement>();
      getVariableForProvider(provider, varsToChange);
    }
    return (
      <VirtualVisitCard
        callPingFederate={callPingFederate}
        cardCTA={cardCTA}
        encryptedPayload={encryptedPayload}
        key={provider.key}
        pfidAdapterId={pfidAdapterId}
        provider={provider}
        refs={refs}
        varsToChange={varsToChange}
      />
    );
  });
  return (
    <React.Fragment>
      <Layout.Stack
        css={{
          marginBottom: '8px',
          width: '100%',
        }}
        grow
        space={0}
      >
        {virtualVisitProviders?.length ? (
          showCarouselInMobile && phoneScreen ? (
            <Carousel
              css={{
                ...mixedResultCarouselStyle(
                  isExpandViewAllEnabled
                    ? virtualVisitProviders?.length + 1
                    : virtualVisitProviders?.length,
                  winWidth,
                  false,
                  isExpandViewAllEnabled
                ),
              }}
              minimal
              nextSlideOnClick={() => makeAllSlidesAriaHidden()}
              noLoop
              prevSlideOnClick={() => makeAllSlidesAriaHidden()}
              slideIndexOnClick={() => makeAllSlidesAriaHidden()}
              slides={virtualVisitCards.map((slide) => (
                <Slide
                  css={{
                    'abyss-slide-container': {
                      marginLeft: 0,
                      background: 'transparent',
                    },
                  }}
                  key={slide.props.provider.id}
                >
                  <Slide.Container css={{ padding: 0 }}>
                    {slide}
                  </Slide.Container>
                </Slide>
              ))}
              slidesPerView={slidesPerView}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '24px',
                flexWrap: 'wrap',
              }}
            >
              {virtualVisitCards.map((slide) => slide)}
            </div>
          )
        ) : (
          <NoResultsFoundContainer>
            <Text>{'No Results Found'}</Text>
          </NoResultsFoundContainer>
        )}
      </Layout.Stack>
    </React.Fragment>
  );
};
